import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { alpha, Box, Button, ButtonProps, Theme } from '@mui/material';
import React from 'react';

export function getGlassCtaButtonSx(theme: Theme) {
    return {
        px: { xs: 2, sm: 3 },
        py: 1.5,
        pl: { xs: 1.25, sm: 1.5 },
        pr: { xs: 2, sm: 2.5 },
        borderRadius: 999,
        textTransform: 'none' as const,
        fontWeight: 700,
        fontSize: { xs: '1rem', sm: '1.05rem' },
        letterSpacing: 0.02,
        color: 'text.primary',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04)} 100%)`,
        backdropFilter: 'blur(12px)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.35 : 0.08)}`,
        gap: 1.25,
        transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
        '& .MuiButton-startIcon': {
            marginRight: 0.5,
            marginLeft: 0,
            transition: 'transform 0.25s ease',
        },
        '& .MuiButton-endIcon': {
            marginLeft: 0.25,
            transition: 'transform 0.2s ease',
        },
        '&:hover': {
            borderColor: theme.palette.primary.main,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.16)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08)} 100%)`,
            boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.35 : 0.12)}, 0 4px 16px ${alpha(theme.palette.primary.main, 0.09)}`,
            transform: 'translateY(-3px)',
            '& .MuiButton-startIcon': {
                transform: 'scale(1.06)',
            },
            '& .MuiButton-endIcon': {
                transform: 'translateX(5px)',
            },
        },
    };
}

function leadingIconCircleSx(theme: Theme) {
    return {
        display: 'inline-flex' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: 'primary.main' as const,
        color: 'common.white' as const,
        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
    };
}

export type GlassCtaButtonProps = Omit<ButtonProps, 'startIcon' | 'endIcon' | 'variant'> & {
    /** Icon inside the circular badge (e.g. MUI icon with fontSize). */
    leadingIcon: React.ReactNode;
    /** Default: right chevron. Set false to hide. */
    showTrailingChevron?: boolean;
};

const GlassCtaButton = React.forwardRef<HTMLButtonElement, GlassCtaButtonProps>(function GlassCtaButton(
    { leadingIcon, showTrailingChevron = true, children, sx, ...buttonProps },
    ref,
) {
    return (
        <Button
            ref={ref}
            size="large"
            {...buttonProps}
            startIcon={<Box component="span" sx={(theme) => leadingIconCircleSx(theme)}>{leadingIcon}</Box>}
            endIcon={
                showTrailingChevron ? <ChevronRightRounded sx={{ fontSize: 26, color: 'primary.main' }} /> : undefined
            }
            sx={[(theme) => getGlassCtaButtonSx(theme), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
            {children}
        </Button>
    );
});

export default GlassCtaButton;
